import React, { useState, useEffect, useContext } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AuthContext } from './AuthContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, Chip, Grid, Paper, CircularProgress, Alert,
  FormControlLabel, Checkbox, IconButton, Tooltip, Autocomplete,
  List, ListItem, ListItemText, ListItemSecondaryAction, Divider,
  Snackbar,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  Note as NoteIcon,
  Description as DocumentIcon,
  Payment as PaymentIcon,
  Help as HelpIcon,
  Schedule as ScheduleIcon,
  AttachFile as AttachIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import api, { enhancedApi } from '../api';
import CalendarSlotPicker from './CalendarSlotPicker';
import FeatureGate from './FeatureGate';

const AI_WORKFLOW_OPTIONS = [
  { value: '1040 individual', label: '1040 Individual' },
  { value: 'S-Corp return', label: 'S-Corp Return' },
  { value: 'C-Corp return', label: 'C-Corp Return' },
  { value: 'Partnership return', label: 'Partnership Return' },
  { value: 'Trust return', label: 'Trust Return' },
  { value: 'Bookkeeping setup', label: 'Bookkeeping Setup' },
];

const contentEditorModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    ['link'],
    [{ 'color': [] }, { 'background': [] }],
    ['clean']
  ],
};

const EnhancedCommunicationComposer = ({ 
  open, 
  onClose, 
  clientId, 
  clientName, 
  mode = 'create', // 'create', 'reply', 'forward', 'edit'
  communication = null,
  onSuccess,
  onMeetingCreated,
  presetType = null // 'document_request' when Request Documents is clicked
}) => {
  const { userPermissions } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [communicationTypes, setCommunicationTypes] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  const [formData, setFormData] = useState({
    communication_type: '',
    subject: '',
    content: '',
    direction: 'outbound',
    status: 'draft',
    priority: 'medium',
    due_date: '',
    is_follow_up_required: false,
    follow_up_date: ''
  });

  const [recipients, setRecipients] = useState({
    to: [],
    cc: [],
    bcc: []
  });

  const [newRecipient, setNewRecipient] = useState({
    email: '',
    name: '',
    type: 'to'
  });

  // Meeting/Appointment slot picker state
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraftSnackbar, setAiDraftSnackbar] = useState({ open: false, message: '' });
  const [aiDocWorkflowType, setAiDocWorkflowType] = useState('1040 individual');
  const [aiDocListLoading, setAiDocListLoading] = useState(false);
  const [aiDocResults, setAiDocResults] = useState([]);
  const [aiDocSelected, setAiDocSelected] = useState({});
  const [aiDocSnackbar, setAiDocSnackbar] = useState({ open: false, message: '' });
  const [aiDocSending, setAiDocSending] = useState(false);

  const isMeetingOrAppointment = () => {
    const typeName = communicationTypes.find(t => t.id === formData.communication_type)?.name || '';
    return typeName === 'Meeting' || typeName === 'Appointment';
  };

  const isContentEmpty = (html) => {
    if (!html) return true;
    const stripped = (html || '').replace(/<[^>]*>/g, '').trim();
    return !stripped;
  };

  const getCommunicationIcon = (typeName) => {
    const iconMap = {
      'Email': <EmailIcon />,
      'Phone Call': <PhoneIcon />,
      'Meeting': <EventIcon />,
      'Note': <NoteIcon />,
      'Document Request': <DocumentIcon />,
      'Payment Reminder': <PaymentIcon />,
      'Tax Question': <HelpIcon />,
      'Appointment': <ScheduleIcon />
    };
    return iconMap[typeName] || <NoteIcon />;
  };

  const fetchCommunicationTypes = async () => {
    try {
      const response = await api.get('/api/communication-types/');
      if (response.data && response.data.types) {
        setCommunicationTypes(response.data.types);
      }
    } catch (error) {
      console.error('Error fetching communication types:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get('/api/email-templates/');
      if (response.data && response.data.templates) {
        setEmailTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchClientContacts = async () => {
    try {
      const response = await api.get(`/api/clients/${clientId}/contacts/`);
      if (response.data && response.data.contacts) {
        setClientContacts(response.data.contacts);
        
        // Auto-populate primary contact in "To" field (always set when we have data - avoids stale closure)
        const primaryContact = response.data.contacts.find(contact => contact.is_primary);
        if (primaryContact) {
          setRecipients(prev => ({
            ...prev,
            to: [{
              email: primaryContact.email,
              name: primaryContact.name,
              is_primary_contact: true
            }]
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching client contacts:', error);
    }
  };

  // Fetch client data and assigned users
  const fetchClientData = async () => {
    try {
      const response = await api.get(`/api/clients/${clientId}/cc-users/`);
      if (response.data && response.data.success) {
        // Auto-populate TO field with client email (always set when we have data - avoids stale closure)
        if (response.data.client_email) {
          setRecipients(prev => ({
            ...prev,
            to: [{
              email: response.data.client_email,
              name: response.data.client_name || response.data.client_email,
              is_primary_contact: true
            }]
          }));
        }
        
        // Set assigned users for CC selection
        setTeamMembers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/api/company/users-for-cc/');
      if (response.data && response.data.success && response.data.users) {
        setTeamMembers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchCalendarAndMeetingTypes = async () => {
    try {
      const [statusRes, typesRes] = await Promise.all([
        enhancedApi.getCalendarStatus(),
        enhancedApi.getMeetingTypes()
      ]);
      setCalendarConnected(statusRes.data?.connected || false);
      setMeetingTypes(typesRes.data?.meeting_types || []);
      if (typesRes.data?.meeting_types?.length && !selectedMeetingType) {
        setSelectedMeetingType(typesRes.data.meeting_types[0].id);
      }
    } catch {
      setCalendarConnected(false);
      setMeetingTypes([]);
    }
  };

  const fetchSlots = async () => {
    if (!clientId) return;
    try {
      setSlotsLoading(true);
      const res = await enhancedApi.getCalendarSlots({
        client_id: clientId,
        meeting_type_id: selectedMeetingType || undefined,
        days: 30,
      });
      setSlots(res.data?.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCommunicationTypes();
      fetchEmailTemplates();
      if (clientId) {
        fetchClientContacts();
        fetchClientData(); // Auto-populate TO field with client email and get assigned users
      }
      
      if (mode === 'edit' && communication) {
        setFormData({
          communication_type: communication.communication_type,
          subject: communication.subject,
          content: communication.content,
          direction: communication.direction,
          status: communication.status,
          priority: communication.priority,
          due_date: communication.due_date ? communication.due_date.split('T')[0] : '',
          is_follow_up_required: communication.is_follow_up_required,
          follow_up_date: communication.follow_up_date ? communication.follow_up_date.split('T')[0] : '',
          document_requests: communication.document_requests || []
        });
        
        // Set recipients from existing communication
        if (communication.recipients) {
          const toRecipients = communication.recipients.filter(r => r.recipient_type === 'to');
          const ccRecipients = communication.recipients.filter(r => r.recipient_type === 'cc');
          const bccRecipients = communication.recipients.filter(r => r.recipient_type === 'bcc');
          
          setRecipients({
            to: toRecipients,
            cc: ccRecipients,
            bcc: bccRecipients
          });
        }
      } else if (mode === 'reply' && communication) {
        const originalHtml = (communication.content || '').includes('<')
          ? communication.content
          : `<p>${(communication.content || '').replace(/\n/g, '</p><p>')}</p>`;
        setFormData(prev => ({
          ...prev,
          subject: `Re: ${communication.subject}`,
          content: `<p><br></p><p>--- Original Message ---</p>${originalHtml}`
        }));
        
        // Set original sender as recipient
        if (communication.recipients && communication.recipients.length > 0) {
          const originalRecipients = communication.recipients.filter(r => r.recipient_type === 'to');
          setRecipients(prev => ({
            ...prev,
            to: originalRecipients
          }));
        }
      } else if (mode === 'forward' && communication) {
        const originalHtml = (communication.content || '').includes('<')
          ? communication.content
          : `<p>${(communication.content || '').replace(/\n/g, '</p><p>')}</p>`;
        setFormData(prev => ({
          ...prev,
          subject: `Fwd: ${communication.subject}`,
          content: `<p><br></p><p>--- Forwarded Message ---</p>${originalHtml}`
        }));
      }
    }
  }, [open, mode, communication, clientId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      subject: template.subject,
      content: template.content
    }));
  };

  useEffect(() => {
    if (!open && presetType === 'document_request') {
      setAiDocResults([]);
      setAiDocSelected({});
      setAiDocSnackbar({ open: false, message: '' });
    }
  }, [open, presetType]);

  useEffect(() => {
    if (open && presetType === 'document_request') {
      setFormData(prev => ({
        ...prev,
        subject: prev.subject || 'Document Request',
        content: prev.content || '<p>Please upload the requested documents at your earliest convenience.</p>'
      }));
    }
  }, [open, presetType]);

  // Auto-select Email communication type when opening for document request
  useEffect(() => {
    if (open && presetType === 'document_request' && communicationTypes.length > 0) {
      const emailType = communicationTypes.find(t => t.name === 'Email' || t.name?.toLowerCase() === 'email')
        || communicationTypes[0];
      setFormData(prev => ({ ...prev, communication_type: emailType.id }));
    }
  }, [open, presetType, communicationTypes]);

  useEffect(() => {
    if (open && isMeetingOrAppointment()) {
      fetchCalendarAndMeetingTypes();
    }
  }, [open, formData.communication_type]);

  useEffect(() => {
    if (open && isMeetingOrAppointment() && calendarConnected && clientId) {
      fetchSlots();
    } else {
      setSlots([]);
      setSelectedSlot(null);
    }
  }, [open, formData.communication_type, calendarConnected, clientId, selectedMeetingType]);

  const addRecipient = (typeOverride) => {
    const targetType = typeOverride ?? newRecipient.type;
    if (newRecipient.email) {
      const recipient = {
        email: newRecipient.email,
        name: newRecipient.name || newRecipient.email,
        is_primary_contact: false
      };
      
      setRecipients(prev => ({
        ...prev,
        [targetType]: [...prev[targetType], recipient]
      }));
      
      setNewRecipient({ email: '', name: '', type: 'to' });
    }
  };

  const removeRecipient = (type, index) => {
    setRecipients(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const addContactAsRecipient = (contact, type = 'to') => {
    const recipient = {
      email: contact.email,
      name: contact.name,
      is_primary_contact: contact.is_primary
    };
    
    setRecipients(prev => ({
      ...prev,
      [type]: [...prev[type], recipient]
    }));
  };

  const addTeamMemberAsRecipient = (teamMember, type = 'cc') => {
    const recipient = {
      id: teamMember.id,
      email: teamMember.email,
      name: teamMember.display_name || teamMember.name,
      is_primary_contact: false
    };
    
    setRecipients(prev => ({
      ...prev,
      [type]: [...prev[type], recipient]
    }));
  };

  const handleSubmit = async () => {
    const isMeeting = isMeetingOrAppointment();
    if (!isMeeting && recipients.to.length === 0) {
      setError('Please add a recipient email in the To field.');
      return;
    }
    if (!formData.subject?.trim()) {
      setError('Please enter a subject.');
      return;
    }
    if (!isMeeting && isContentEmpty(formData.content)) {
      setError('Please enter content.');
      return;
    }
    try {
      setLoading(true);
      setError(null);

      if (isMeeting && (mode === 'create' || mode === 'reply' || mode === 'forward')) {
        // Meeting/Appointment: create communication, then optionally create meeting with calendar
        const createData = {
          client: clientId,
          communication_type: formData.communication_type,
          subject: formData.subject,
          content: formData.content || `Meeting with ${clientName || 'client'}`,
          direction: 'outbound',
          status: 'sent',
          priority: formData.priority,
          due_date: formData.due_date || null,
          is_follow_up_required: formData.is_follow_up_required,
          follow_up_date: formData.follow_up_date || null,
        };
        const commResponse = await api.post(`/api/clients/${clientId}/communications/create/`, createData);
        const communicationId = commResponse.data?.id;

        if (selectedSlot && communicationId) {
          await enhancedApi.createMeeting({
            client_id: clientId,
            meeting_type_id: selectedMeetingType || null,
            scheduled_at: selectedSlot.start,
            notes: formData.content || '',
            communication_id: communicationId,
          });
          onMeetingCreated && onMeetingCreated();
        }

        onSuccess && onSuccess(commResponse.data);
        onClose();
        setFormData({
          communication_type: '',
          subject: '',
          content: '',
          direction: 'outbound',
          status: 'draft',
          priority: 'medium',
          due_date: '',
          is_follow_up_required: false,
          follow_up_date: ''
        });
        setRecipients({ to: [], cc: [], bcc: [] });
        setSelectedSlot(null);
      } else if (presetType === 'document_request' || !isMeeting) {
        // Email or document request: use send-email
        const submitData = {
          ...formData,
          to_email: recipients.to.length > 0 ? recipients.to[0].email : '',
          due_date: formData.due_date || null,
          follow_up_date: formData.follow_up_date || null,
          cc_user_ids: recipients.cc.map(r => r.id).filter(id => id),
          bcc_user_ids: recipients.bcc.map(r => r.id).filter(id => id),
          is_document_request: presetType === 'document_request'
        };
        const response = await api.post(`/api/clients/${clientId}/send-email/`, submitData);
        if (response.status === 201 || response.status === 200) {
          onSuccess && onSuccess(response.data);
          onClose();
          setFormData({
            communication_type: '',
            subject: '',
            content: '',
            direction: 'outbound',
            status: 'draft',
            priority: 'medium',
            due_date: '',
            is_follow_up_required: false,
            follow_up_date: ''
          });
          setRecipients({ to: [], cc: [], bcc: [] });
        }
      } else if (mode === 'edit') {
        const response = await api.patch(`/api/communications/${communication.id}/status/`, formData);
        if (response.status === 200) {
          onSuccess && onSuccess(response.data);
          onClose();
        }
      }
    } catch (err) {
      console.error('Error saving communication:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to save communication');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const threadId = communication?.email_thread_id ?? communication?.email_thread?.id;

  const handleAiDraftClick = async () => {
    if (!threadId) return;
    setAiDraftLoading(true);
    try {
      const res = await enhancedApi.draftEmailResponse(threadId);
      const draft = res.data?.draft || '';
      const html = (draft || '').includes('<') ? draft : `<p>${(draft || '').replace(/\n/g, '</p><p>')}</p>`;
      handleInputChange('content', html);
    } catch {
      setAiDraftSnackbar({ open: true, message: 'Draft unavailable, compose manually' });
    } finally {
      setAiDraftLoading(false);
    }
  };

  const handleAiDocGenerate = async () => {
    if (!clientId) return;
    setAiDocListLoading(true);
    setAiDocResults([]);
    setAiDocSelected({});
    setAiDocSnackbar({ open: false, message: '' });
    try {
      const res = await enhancedApi.generateDocumentRequests(clientId, aiDocWorkflowType);
      const docs = res.data?.documents || [];
      setAiDocResults(docs);
      const sel = {};
      docs.forEach((d, i) => { sel[i] = true; });
      setAiDocSelected(sel);
    } catch {
      setAiDocSnackbar({ open: true, message: 'Document suggestions unavailable' });
      setAiDocResults([]);
    } finally {
      setAiDocListLoading(false);
    }
  };

  const handleAiDocToggle = (idx) => {
    setAiDocSelected(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleAiDocSelectAll = () => {
    const sel = {};
    aiDocResults.forEach((_, i) => { sel[i] = true; });
    setAiDocSelected(sel);
  };

  const handleAiDocDeselectAll = () => {
    setAiDocSelected({});
  };

  const handleSendSelectedAsRequests = async () => {
    const selected = aiDocResults.filter((_, i) => aiDocSelected[i]);
    if (selected.length === 0) return;
    if (!clientId) return;
    const emailType = communicationTypes.find(t => t.name === 'Email' || t.name?.toLowerCase() === 'email') || communicationTypes[0];
    if (!emailType) {
      setError('Form is still loading. Please try again.');
      return;
    }
    const dueDate = formData.due_date || (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();
    setAiDocSending(true);
    setError(null);
    try {
      const document_requests = selected.map(d => ({
        document_type: d.document_name,
        description: d.description || '',
        due_date: dueDate,
      }));
      const createData = {
        client: clientId,
        communication_type: emailType.id,
        subject: formData.subject || 'Document Request',
        content: formData.content || '<p>Please upload the requested documents at your earliest convenience.</p>',
        direction: 'outbound',
        status: 'draft',
        priority: formData.priority || 'medium',
        due_date: dueDate,
        document_requests,
      };
      await api.post(`/api/clients/${clientId}/communications/create/`, createData);
      onSuccess && onSuccess({});
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to create document requests');
    } finally {
      setAiDocSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {mode === 'create' && 'Send Email'}
            {mode === 'reply' && 'Reply to Email'}
            {mode === 'forward' && 'Forward Email'}
            {mode === 'edit' && 'Edit Communication'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {clientName && (
          <Typography variant="body2" color="text.secondary">
            Client: {clientName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Communication Type */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Communication Type</InputLabel>
              <Select
                value={formData.communication_type}
                onChange={(e) => handleInputChange('communication_type', e.target.value)}
                label="Communication Type"
              >
                {communicationTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    <Box display="flex" alignItems="center">
                      {getCommunicationIcon(type.name)}
                      <Typography sx={{ ml: 1 }}>{type.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Priority */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Book a time - Meeting/Appointment slot picker */}
          {isMeetingOrAppointment() && mode === 'create' && (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" />
                  Book a time from your calendar
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Pick an available slot to schedule the meeting. Your calendar will be blocked when you save.
                </Typography>
                {!calendarConnected ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1.5, border: '1px solid', borderColor: 'warning.light' }}>
                    <ScheduleIcon sx={{ color: 'warning.main', fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      Google Calendar is not connected.{' '}
                      <a href="/settings/integrations" style={{ color: 'inherit', fontWeight: 600 }}>
                        Connect it in Settings
                      </a>{' '}
                      to see available slots.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
                      <InputLabel>Meeting Type</InputLabel>
                      <Select
                        value={selectedMeetingType}
                        onChange={(e) => setSelectedMeetingType(e.target.value)}
                        label="Meeting Type"
                      >
                        {meetingTypes.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name} ({t.duration_minutes} min)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {slotsLoading ? (
                      <Box display="flex" alignItems="center" gap={1} py={1}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading available slots...</Typography>
                      </Box>
                    ) : slots.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No available slots in the next 30 days.
                      </Typography>
                    ) : (
                      <CalendarSlotPicker
                        slots={slots}
                        selectedSlot={selectedSlot}
                        onSelectSlot={setSelectedSlot}
                      />
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          )}

          {/* Recipients Section - hide for Meeting/Appointment */}
          {!isMeetingOrAppointment() && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recipients
              </Typography>
              
              {/* To Recipients */}
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  To:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  {recipients.to.map((recipient, index) => (
                    <Chip
                      key={index}
                      label={`${recipient.name} (${recipient.email})`}
                      color="primary"
                      icon={<PersonIcon />}
                      variant="filled"
                      onDelete={() => removeRecipient('to', index)}
                    />
                  ))}
                </Box>
                {recipients.to.length === 0 && (
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Email"
                      value={newRecipient.email}
                      onChange={(e) => setNewRecipient(prev => ({ ...prev, email: e.target.value }))}
                      sx={{ minWidth: 200 }}
                    />
                    <TextField
                      size="small"
                      placeholder="Name (optional)"
                      value={newRecipient.name}
                      onChange={(e) => setNewRecipient(prev => ({ ...prev, name: e.target.value }))}
                      sx={{ minWidth: 150 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => addRecipient('to')}
                      disabled={!newRecipient.email}
                    >
                      Add
                    </Button>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {recipients.to.length > 0 ? 'Client email (click X to remove)' : 'Add recipient email if not auto-populated'}
                </Typography>
              </Box>

              {/* CC Recipients with Dropdown */}
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  CC:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  {recipients.cc.map((recipient, index) => (
                    <Chip
                      key={index}
                      label={`${recipient.name} (${recipient.role})`}
                      onDelete={() => removeRecipient('cc', index)}
                      color="secondary"
                      variant="filled"
                    />
                  ))}
                </Box>
                
                {/* CC Selection Dropdown */}
                {teamMembers.length > 0 && (
                  <Autocomplete
                    multiple
                    options={teamMembers}
                    getOptionLabel={(option) => `${option.display_name} (${option.role})`}
                    value={teamMembers.filter(member => 
                      recipients.cc.some(r => r.id === member.id)
                    )}
                    onChange={(event, newValue) => {
                      // Update CC recipients based on selection
                      const newCCRecipients = newValue.map(member => ({
                        id: member.id,
                        email: member.email,
                        name: member.display_name,
                        role: member.role,
                        is_primary_contact: false
                      }));
                      setRecipients(prev => ({
                        ...prev,
                        cc: newCCRecipients
                      }));
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box display="flex" alignItems="center" width="100%">
                          <Box flexGrow={1}>
                            <Typography variant="body2">
                              {option.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email} • {option.role}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={option.role} 
                            color={option.is_admin ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Admins and Assigned Users"
                        placeholder="Choose users to CC..."
                        size="small"
                        variant="outlined"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={`${option.display_name} (${option.role})`}
                          color="secondary"
                          size="small"
                        />
                      ))
                    }
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              {/* BCC Recipients with Dropdown */}
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  BCC:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  {recipients.bcc.map((recipient, index) => (
                    <Chip
                      key={index}
                      label={`${recipient.name} (${recipient.role})`}
                      onDelete={() => removeRecipient('bcc', index)}
                      color="default"
                      variant="outlined"
                    />
                  ))}
                </Box>
                
                {/* BCC Selection Dropdown */}
                {teamMembers.length > 0 && (
                  <Autocomplete
                    multiple
                    options={teamMembers}
                    getOptionLabel={(option) => `${option.display_name} (${option.role})`}
                    value={teamMembers.filter(member => 
                      recipients.bcc.some(r => r.id === member.id)
                    )}
                    onChange={(event, newValue) => {
                      // Update BCC recipients based on selection
                      const newBCCRecipients = newValue.map(member => ({
                        id: member.id,
                        email: member.email,
                        name: member.display_name,
                        role: member.role,
                        is_primary_contact: false
                      }));
                      setRecipients(prev => ({
                        ...prev,
                        bcc: newBCCRecipients
                      }));
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box display="flex" alignItems="center" width="100%">
                          <Box flexGrow={1}>
                            <Typography variant="body2">
                              {option.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email} • {option.role}
                            </Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={option.role} 
                            color={option.is_admin ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Users for BCC"
                        placeholder="Choose users to BCC..."
                        size="small"
                        variant="outlined"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={`${option.display_name} (${option.role})`}
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      ))
                    }
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>



              {/* Client Contacts Quick Add */}
              {clientContacts.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quick Add Client Contacts:
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {clientContacts.map((contact, index) => (
                      <Chip
                        key={index}
                        label={contact.name || contact.email}
                        onClick={() => addContactAsRecipient(contact, 'to')}
                        variant="outlined"
                        size="small"
                        icon={contact.is_primary ? <PersonIcon /> : undefined}
                      />
                    ))}
                  </Box>
              </Box>
            )}
            </Paper>
          </Grid>
          )}

          {/* AI Document List - only when document request preset */}
          {presetType === 'document_request' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesomeIcon color="primary" />
                AI Document List
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Workflow Type</InputLabel>
                  <Select
                    value={aiDocWorkflowType}
                    onChange={(e) => setAiDocWorkflowType(e.target.value)}
                    label="Workflow Type"
                    disabled={aiDocListLoading}
                  >
                    {AI_WORKFLOW_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={aiDocListLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  onClick={handleAiDocGenerate}
                  disabled={aiDocListLoading || !clientId}
                >
                  {aiDocListLoading ? 'Generating document list...' : 'Generate'}
                </Button>
              </Box>
              {aiDocResults.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button size="small" variant="text" onClick={handleAiDocSelectAll}>Select All</Button>
                    <Button size="small" variant="text" onClick={handleAiDocDeselectAll}>Deselect All</Button>
                  </Box>
                  <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, py: 0 }}>
                    {aiDocResults.map((doc, idx) => (
                      <ListItem key={idx} sx={{ py: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!aiDocSelected[idx]}
                              onChange={() => handleAiDocToggle(idx)}
                              size="small"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{doc.document_name}</Typography>
                              {doc.description && (
                                <Typography variant="caption" color="text.secondary">{doc.description}</Typography>
                              )}
                              {doc.required && (
                                <Chip label="Required" size="small" color="primary" variant="outlined" sx={{ alignSelf: 'flex-start', mt: 0.5 }} />
                              )}
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={aiDocSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={handleSendSelectedAsRequests}
                    disabled={aiDocSending || Object.values(aiDocSelected).every(v => !v)}
                    sx={{ mt: 2 }}
                  >
                    {aiDocSending ? 'Creating...' : 'Send Selected as Requests'}
                  </Button>
                </>
              )}
              {!aiDocListLoading && aiDocResults.length === 0 && aiDocWorkflowType && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Select a workflow type and click Generate to see suggested documents.
                </Typography>
              )}
            </Paper>
          </Grid>
          )}

          {/* Email Templates */}
          {formData.communication_type && 
           communicationTypes.find(t => t.id === formData.communication_type)?.name === 'Email' && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Email Template (Optional)</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    const template = emailTemplates.find(t => t.id === e.target.value);
                    if (template) handleTemplateSelect(template);
                  }}
                  label="Email Template (Optional)"
                >
                  <MenuItem value="">No Template</MenuItem>
                  {emailTemplates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name} ({template.category})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Subject */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subject"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              required
            />
          </Grid>

          {/* Content - Rich text editor (same as Inbox) */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ display: 'block' }}>Content</Typography>
              {mode === 'reply' && !isMeetingOrAppointment() && (
                <FeatureGate feature="ai_features">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={aiDraftLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    onClick={handleAiDraftClick}
                    disabled={aiDraftLoading || !threadId}
                  >
                    {aiDraftLoading ? 'Drafting...' : 'AI Draft Reply'}
                  </Button>
                </FeatureGate>
              )}
            </Box>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                '& .ql-container': { minHeight: 200, fontSize: 15 },
                '& .ql-editor': { minHeight: 200 }
              }}
            >
              <ReactQuill
                theme="snow"
                value={formData.content || ''}
                onChange={(value) => handleInputChange('content', value)}
                placeholder="Type your message..."
                modules={contentEditorModules}
              />
            </Box>
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Due Date (Optional)"
              type="date"
              value={formData.due_date}
              onChange={(e) => handleInputChange('due_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Follow-up */}
          <Grid item xs={12} sm={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                borderColor: formData.is_follow_up_required ? 'warning.main' : 'divider',
                bgcolor: formData.is_follow_up_required ? 'warning.lighter' : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_follow_up_required}
                    onChange={(e) => handleInputChange('is_follow_up_required', e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: formData.is_follow_up_required ? 600 : 400 }}>
                    Requires Follow-Up
                  </Typography>
                }
              />
              {formData.is_follow_up_required && (
                <TextField
                  fullWidth
                  label="Follow-up Date"
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => handleInputChange('follow_up_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ mt: 1 }}
                  helperText="You'll see this in the Follow-Ups tab"
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : (isMeetingOrAppointment() ? <EventIcon /> : <SendIcon />)}
          disabled={
            loading || !formData.communication_type || !formData.subject ||
            (presetType === 'document_request' && (isContentEmpty(formData.content) || recipients.to.length === 0)) ||
            (!isMeetingOrAppointment() && presetType !== 'document_request' && (isContentEmpty(formData.content) || recipients.to.length === 0))
          }
        >
          {loading ? (isMeetingOrAppointment() ? 'Saving...' : 'Sending...') : (presetType === 'document_request' ? 'Send Document Request' : isMeetingOrAppointment() ? 'Save Meeting' : 'Send Email')}
        </Button>
      </DialogActions>
      <Snackbar
        open={aiDraftSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAiDraftSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={aiDraftSnackbar.message}
      />
      <Snackbar
        open={aiDocSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAiDocSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={aiDocSnackbar.message}
      />
    </Dialog>
  );
};

export default EnhancedCommunicationComposer;
